class ExpandableRow extends HTMLElement {
  constructor() {
    super()
    this.button = this.querySelector(".expandable-trigger")
    this.wrapper = this.querySelector(".expandable-wrapper")
  }

  connectedCallback() {
    if (!this.button || !this.wrapper) return

    // 1. Inject CSS (Scoped & performant)
    this.injectStyles()

    // 2. Set Initial Accessibility State
    // We check if it was hardcoded as open in HTML, otherwise close it
    const isOpen = this.hasAttribute("open")
    this.button.setAttribute("aria-expanded", isOpen)
    this.wrapper.setAttribute("aria-hidden", !isOpen)

    // 3. Bind Events
    this.button.addEventListener("click", this.toggle.bind(this))
  }

  toggle() {
    const isOpen = this.hasAttribute("open")

    if (isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.setAttribute("open", "")
    this.button.setAttribute("aria-expanded", "true")
    this.wrapper.setAttribute("aria-hidden", "false")
  }

  close() {
    this.removeAttribute("open")
    this.button.setAttribute("aria-expanded", "false")
    // We wait for the transition to finish before hiding purely for screen readers,
    // but aria-hidden="true" is usually enough immediate feedback.
    this.wrapper.setAttribute("aria-hidden", "true")
  }

  injectStyles() {
    // Singleton check: Only inject styles once per page load
    if (document.querySelector("#expandable-row-styles")) return

    const style = document.createElement("style")
    style.id = "expandable-row-styles"
    style.textContent = `
      expandable-row {
        display: block;
      }
        .faq-item{
        background-color: white;
        border-radius: 0.5rem;
        overflow: hidden;
        }

      /* --- Trigger (Title) --- */
      .expandable-trigger {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: white;
        border-radius: 0.5rem;
        padding: 1.125rem 1.5rem;
        border: none;
        cursor: pointer;
        text-align: left;
        font-family: inherit; /* Inherit theme font */
        color: currentColor;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        -webkit-tap-highlight-color: transparent;
      }
        .faq-item:has(.expandable-trigger:hover)  * {
         background-color: #FCFCFC;
        }

      .expandable-title {
        font-size: 1rem;
        font-weight: 500;
        color: #474747;
      }
        .expandable-rte{
        font-size: 0.875rem;
        }

      /* --- Icon Animation (CSS-only) --- */
      .expandable-icon {
        width: 12px;
        height: 12px;
        position: relative;
        transition: transform 0.3s ease;
      }
      
      /* Create a Plus (+) sign using pseudo-elements */
      .expandable-icon::before,
      .expandable-icon::after {
        content: '';
        position: absolute;
        background-color: currentColor;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      
      .expandable-icon::before { width: 100%; height: 2px; } /* Horizontal line */
      .expandable-icon::after { height: 100%; width: 2px; transition: transform 0.3s ease; } /* Vertical line */

      /* Rotate to Minus (-) when open */
      expandable-row[open] .expandable-icon::after {
        transform: translate(-50%, -50%) rotate(90deg);
      }

      /* --- The Grid Animation Logic --- */
      .expandable-wrapper {
        display: grid;
        /* 0fr means the row has 0 height */
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.3s ease-out, visibility 0.3s;
        visibility: hidden; /* Hides from tab order when closed */
      }

      expandable-row[open] .expandable-wrapper {
        /* 1fr means the row takes up 1 fraction of space (auto height) */
        grid-template-rows: 1fr;
        visibility: visible;
      }

      .expandable-content {
        /* CRITICAL: overflow-hidden is required for the grid animation to work */
        overflow: hidden;
        min-height: 0;
        background-color: white;
     
        padding: 0 1.5rem;
      }

      /* Add padding inside the content, not the wrapper, to avoid jumpiness */
      .expandable-content > div {
        padding-bottom: 1rem;
        opacity: 0;
        transform: translateY(-10px);
        transition: opacity 0.3s ease, transform 0.3s ease;
      }
      
      /* Fade in content slightly after opening */
      expandable-row[open] .expandable-content > div {
        opacity: 1;
        transform: translateY(0);
        /* slight delay to wait for grid to start opening */
        transition-delay: 0.1s; 
      }

      /* --- Reduced Motion Support --- */
      @media (prefers-reduced-motion: reduce) {
        .expandable-wrapper, 
        .expandable-icon, 
        .expandable-icon::after,
        .expandable-content > div {
          transition: none !important;
        }
      }
    `
    document.head.appendChild(style)
  }
}

if (!customElements.get("expandable-row")) {
  customElements.define("expandable-row", ExpandableRow)
}
